import React from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Title } from "../../../common";
import { SubTitle } from "../../../common/typography/SubTitle";
import styled from "styled-components";
import { breakpoints, fonts } from "../../common/mixins";
import Button from "../../common/Button";
import { Carousel } from "../../common/Carousel";
import {
  ProductActions,
  ProductCard,
  getAvailableOptions,
} from "./ProductCard";
import { ProductDrawer } from "./ProductDrawer";
import { ProductDescriptionModal } from "./ProductDescriptionModal";
import useModal from "../../../hooks/useModal";
import { StoreProduct, useReservationStore } from "../../../stores/useReservationStore";
import Overlay, { useIsOverlayDrawer } from "../../common/Overlay";
import {
  ButtonsWrapper,
  ModalContainer,
  ModalTitle,
} from "../../../common/modal.styles";
import {
  getScopedMaxQty,
  getScopedMinQty,
  getScopedQty,
} from "../../../pageComponents/reservationPage/utils/productQuantities";
import { Tooltip } from "../../common/Tooltip";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import { Section } from "../../../pageComponents/reservation-shared-styles";

// How long the button shows its "Added" state before reverting to "Add".
const ADDED_STATE_DURATION_MS = 3000;
// Small pause before auto-closing the drawer/modal after Add, so the close
// doesn't feel instantaneous/jarring.
const CLOSE_DELAY_MS = 1000;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TicketProductsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TicketProductsTitle = styled.h3`
  ${fonts.smallTitle()}
  margin: 0;
`;

interface AgeVerificationModalProps {
  onConfirm: () => void;
  onDecline: () => void;
}

function AgeVerificationModal({
  onConfirm,
  onDecline,
}: AgeVerificationModalProps) {
  const r = useReservationResources();

  return (
    <ModalContainer>
      <ModalTitle>{r.AgeModalDesc}</ModalTitle>
      <ButtonsWrapper>
        <Button
          type="button"
          variant="secondary"
          target="_self"
          height="48px"
          text={r.DeclineAge}
          fullWidth
          onClick={onDecline}
        />
        <Button
          type="button"
          variant="primary"
          target="_self"
          height="48px"
          text={r.ConfirmAge}
          fullWidth
          onClick={onConfirm}
        />
      </ButtonsWrapper>
    </ModalContainer>
  );
}

export interface ProductsRef {
  closeAgeVerification: () => void;
  closeOverlays: () => void;
}

interface ProductsProps {
  currency: string;
  ticketId?: number; // present => per-ticket variant
}

export const Products = React.forwardRef<ProductsRef, ProductsProps>(function Products(
  { currency, ticketId },
  ref,
) {
  const r = useReservationResources();
  const carouselLabel = React.useMemo(
    () => ({ previous: r.CarouselPrevious, next: r.CarouselNext }),
    [r],
  );

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobile - 1}px)`, {
    noSsr: true,
  });
  // When true, the description modal is a drawer and must not have another
  // drawer stacked on top of it.
  const isOverlayDrawer = useIsOverlayDrawer();

  const products = useReservationStore((s) => s.products);
  const setOptionQuantity = useReservationStore((s) => s.setOptionQuantity);
  const setTicketOptionQuantity = useReservationStore(
    (s) => s.setTicketOptionQuantity,
  );
  const orderedTicketIds = useReservationStore((s) => s.orderedTicketIds);
  // Age verification is session-level and shared by every carousel on the
  // page: once the user confirms 18+, they don't need to re-verify.
  const isAgeVerified = useReservationStore((s) => s.isAgeVerified);
  const setAgeVerified = useReservationStore((s) => s.setAgeVerified);

  // Tracked by id, not index: the list is filtered per scope (per-order vs
  // this ticket), so an index would not survive the filter changing.
  const [drawerProductId, setDrawerProductId] = React.useState<number | null>(
    null,
  );
  const [descriptionModalProductId, setDescriptionModalProductId] =
    React.useState<number | null>(null);
  const [openOptionsPopup, setOpenOptionsPopup] = React.useState<number | null>(
    null,
  );

  const [addedProducts, setAddedProducts] = React.useState<Set<number>>(
    new Set(),
  );

  const [productPendingAgeCheck, setProductPendingAgeCheck] =
    React.useState<StoreProduct | null>(null);
  // Which drawer (if any) was closed to show age verification and should be
  // reopened once it's dismissed.
  const [reopenAfterAgeCheck, setReopenAfterAgeCheck] = React.useState<
    "productDrawer" | "descriptionModal" | null
  >(null);

  const {
    isOpen: isAgeVerificationOpen,
    openModal: openAgeVerification,
    closeModal: closeAgeVerification,
  } = useModal(false, { disableScrollLock: true });
  const {
    isOpen: isProductDrawerOpen,
    openModal: openProductDrawer,
    closeModal: closeProductDrawer,
  } = useModal();
  const {
    isOpen: isDescriptionModalOpen,
    openModal: openDescriptionModal,
    closeModal: closeDescriptionModal,
  } = useModal(false, { disableScrollLock: true });

  // Used by the parent (e.g. the leave guard) to dismiss age verification
  // without resuming the pending add or reopening the drawer.
  const cancelAgeVerification = React.useCallback(() => {
    closeAgeVerification();
    setProductPendingAgeCheck(null);
    setReopenAfterAgeCheck(null);
  }, [closeAgeVerification]);

  // Called by the parent (e.g. the leave guard)
  // to dismiss the product drawer and the description modal
  // so another modal can take over
  const closeOverlays = React.useCallback(() => {
    setOpenOptionsPopup(null);
    closeProductDrawer();
    closeDescriptionModal();
  }, [closeProductDrawer, closeDescriptionModal]);

  React.useImperativeHandle(
    ref,
    () => ({ closeAgeVerification: cancelAgeVerification, closeOverlays }),
    [cancelAgeVerification, closeOverlays],
  );

  // Page-wide close request (see requestCloseProductOverlays). Reaches the
  // per-ticket carousels inside the ticket cards too, which a ref from the
  // page can't. Compared against the value seen at mount so a carousel that
  // mounts later (e.g. a ticket card expanded after a request) doesn't react
  // to an old request.
  const overlaysCloseRequest = useReservationStore(
    (s) => s.productOverlaysCloseRequest,
  );
  const handledCloseRequestRef = React.useRef(overlaysCloseRequest);
  React.useEffect(() => {
    if (overlaysCloseRequest === handledCloseRequestRef.current) return;
    handledCloseRequestRef.current = overlaysCloseRequest;
    cancelAgeVerification();
    closeOverlays();
  }, [overlaysCloseRequest, cancelAgeVerification, closeOverlays]);

  const popupRef = React.useRef<HTMLDivElement>(null);
  const timeoutRefs = React.useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // Always holds the latest open/shown-product state, so the delayed close
  // below can check what's true when it fires instead of what was true when
  // Add was clicked (avoids closing the wrong drawer/modal if it changed
  // in between).
  const modalCloseStateRef = React.useRef({
    isProductDrawerOpen: false,
    drawerProductId: null as number | null,
    isDescriptionModalOpen: false,
    descriptionModalProductId: null as number | null,
  });
  modalCloseStateRef.current = {
    isProductDrawerOpen,
    drawerProductId,
    isDescriptionModalOpen,
    descriptionModalProductId,
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setOpenOptionsPopup(null);
      }
    };

    if (openOptionsPopup !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openOptionsPopup]);

  // Esc and swipe-to-close don't fire mousedown, so the click-outside handler
  // above never clears the popup. Reset it whenever the drawer or description
  // modal closes, otherwise it reappears on the card or the next drawer.
  // Only on close: handleAgeConfirmed reopens the description modal and opens
  // the popup in the same update.
  const wasOverlayOpenRef = React.useRef({
    drawer: isProductDrawerOpen,
    description: isDescriptionModalOpen,
  });
  React.useEffect(() => {
    const prev = wasOverlayOpenRef.current;
    if (
      (prev.drawer && !isProductDrawerOpen) ||
      (prev.description && !isDescriptionModalOpen)
    ) {
      setOpenOptionsPopup(null);
    }
    wasOverlayOpenRef.current = {
      drawer: isProductDrawerOpen,
      description: isDescriptionModalOpen,
    };
  }, [isProductDrawerOpen, isDescriptionModalOpen]);

  React.useEffect(() => {
    const timeouts = timeoutRefs.current;
    return () => {
      timeouts.forEach(clearTimeout);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const idSuffix = ticketId !== undefined ? `-${ticketId}` : "";

  const filteredProducts = React.useMemo(
    () =>
      (products ?? []).filter((p) =>
        ticketId !== undefined ? p.isPerTicket : !p.isPerTicket,
      ),
    [products, ticketId],
  );

  // If the user opens a drawer/modal before the delayed close fires (e.g.
  // clicks Add on the product card, then quickly clicks the description link
  // of the same product), that open is a new action and must not be closed by
  // the stale timer.
  const cancelPendingClose = React.useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const addProduct = React.useCallback(
    (productId: number, optionId?: number) => {
      const product = products.find((p) => p.id === productId);
      if (!product) return;

      const options = product.productOptions;
      const option = optionId
        ? options.find((o) => o.id === optionId)
        : options[0];
      if (!option) return;

      const scopedQty = getScopedQty(product, option.id, ticketId);
      const scopedMax = getScopedMaxQty(
        product,
        option,
        ticketId,
        orderedTicketIds,
      );
      if (scopedMax <= scopedQty) return;

      // the jump is per SCOPE (a single ticket for per-ticket products, the
      // whole order otherwise): only the very first unit of this option in
      // this scope starts at the minimum; every later add on the same scope
      // is +1.
      const target =
        scopedQty === 0
          ? getScopedMinQty(product, option, ticketId)
          : scopedQty + 1;
      const newQty = Math.min(target, scopedMax);

      if (ticketId !== undefined) {
        setTicketOptionQuantity(productId, ticketId, option.id, newQty);
      } else {
        setOptionQuantity(productId, option.id, newQty);
      }

      setOpenOptionsPopup(null);
      setAddedProducts((prev) => new Set(prev).add(productId));

      const existing = timeoutRefs.current.get(productId);
      if (existing) clearTimeout(existing);

      const id = setTimeout(() => {
        setAddedProducts((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
        timeoutRefs.current.delete(productId);
      }, ADDED_STATE_DURATION_MS);
      timeoutRefs.current.set(productId, id);

      cancelPendingClose();
      closeTimeoutRef.current = setTimeout(() => {
        const modalState = modalCloseStateRef.current;
        if (
          modalState.isProductDrawerOpen &&
          modalState.drawerProductId === productId
        ) {
          closeProductDrawer();
        }
        if (
          modalState.isDescriptionModalOpen &&
          modalState.descriptionModalProductId === productId
        ) {
          closeDescriptionModal();
        }
        closeTimeoutRef.current = null;
      }, CLOSE_DELAY_MS);
    },
    [
      products,
      ticketId,
      orderedTicketIds,
      setOptionQuantity,
      setTicketOptionQuantity,
      cancelPendingClose,
      closeProductDrawer,
      closeDescriptionModal,
    ],
  );

  // Stable across renders (React.memo on ProductCard only pays off if these
  // identities hold): a new arrow per card in the .map() below would defeat it
  // for every card on every Products render.
  const handleAddClick = React.useCallback(
    (product: StoreProduct) => {
      const options = product.productOptions;
      if (product.isAgeRestricted && !isAgeVerified) {
        setProductPendingAgeCheck(product);
        if (isProductDrawerOpen) {
          setReopenAfterAgeCheck("productDrawer");
          closeProductDrawer();
        } else if (isDescriptionModalOpen && isOverlayDrawer) {
          setReopenAfterAgeCheck("descriptionModal");
          closeDescriptionModal();
        } else {
          setReopenAfterAgeCheck(null);
        }
        openAgeVerification();
        return;
      }
      if (options.length > 1) {
        setOpenOptionsPopup((current) =>
          current === product.id ? null : product.id,
        );
      } else {
        addProduct(product.id, options[0]?.id);
      }
    },
    [
      isAgeVerified,
      isProductDrawerOpen,
      isDescriptionModalOpen,
      isOverlayDrawer,
      addProduct,
      openAgeVerification,
      closeProductDrawer,
      closeDescriptionModal,
    ],
  );

  const handleOptionSelect = React.useCallback(
    (productId: number, optionId: number) => addProduct(productId, optionId),
    [addProduct],
  );

  const handleClosePopup = React.useCallback(
    () => setOpenOptionsPopup(null),
    [],
  );

  const handleCardClick = React.useCallback(
    (product: StoreProduct) => {
      cancelPendingClose();
      setDrawerProductId(product.id);
      openProductDrawer();
    },
    [cancelPendingClose, openProductDrawer],
  );

  const handleDescriptionClick = React.useCallback(
    (product: StoreProduct) => {
      cancelPendingClose();
      setDescriptionModalProductId(product.id);
      openDescriptionModal();
    },
    [cancelPendingClose, openDescriptionModal],
  );

  if (!products) return null;
  if (filteredProducts.length === 0) return null;

  const resumeOverlayIfNeeded = () => {
    if (reopenAfterAgeCheck === "productDrawer") {
      openProductDrawer();
    } else if (reopenAfterAgeCheck === "descriptionModal") {
      openDescriptionModal();
    }
    setReopenAfterAgeCheck(null);
  };

  const handleAgeConfirmed = () => {
    closeAgeVerification();
    setAgeVerified(true);
    resumeOverlayIfNeeded();
    if (!productPendingAgeCheck) return;
    const options = productPendingAgeCheck.productOptions;
    if (options.length > 1) {
      setOpenOptionsPopup(
        openOptionsPopup === productPendingAgeCheck.id
          ? null
          : productPendingAgeCheck.id,
      );
    } else {
      addProduct(productPendingAgeCheck.id, options[0]?.id);
    }
    setProductPendingAgeCheck(null);
  };

  const handleAgeDeclined = () => {
    closeAgeVerification();
    setProductPendingAgeCheck(null);
    resumeOverlayIfNeeded();
  };

  // Looked up fresh from the store on every render so the drawer/modal reflect
  // quantities added from inside them.
  const drawerProduct =
    drawerProductId !== null
      ? filteredProducts.find((p) => p.id === drawerProductId) ?? null
      : null;
  const descriptionModalProduct =
    descriptionModalProductId !== null
      ? filteredProducts.find((p) => p.id === descriptionModalProductId) ??
        null
      : null;

  const renderActions = (product: StoreProduct, interactive: boolean) => (
    <ProductActions
      product={product}
      currency={currency}
      isAdded={addedProducts.has(product.id)}
      availableOptions={getAvailableOptions(product, ticketId, orderedTicketIds)}
      isPopupOpen={openOptionsPopup === product.id}
      popupRef={popupRef}
      idSuffix={idSuffix}
      isMobile={isMobile}
      interactive={interactive}
      onAddClick={handleAddClick}
      onOptionSelect={handleOptionSelect}
      onClosePopup={handleClosePopup}
    />
  );

  const isCardInteractive = !isDescriptionModalOpen && !isProductDrawerOpen;

  const cardElements = filteredProducts.map((product) => (
    <ProductCard
      key={product.id}
      product={product}
      currency={currency}
      isAdded={addedProducts.has(product.id)}
      isPopupOpen={openOptionsPopup === product.id}
      popupRef={popupRef}
      idSuffix={idSuffix}
      ticketId={ticketId}
      orderedTicketIds={orderedTicketIds}
      isMobile={isMobile}
      isInteractive={isCardInteractive}
      onCardClick={handleCardClick}
      onDescriptionClick={handleDescriptionClick}
      onAddClick={handleAddClick}
      onOptionSelect={handleOptionSelect}
      onClosePopup={handleClosePopup}
    />
  ));

  return (
    <>
      {ticketId !== undefined ? (
        <TicketProductsSection id={`ticket-products${idSuffix}`}>
          <div>
            <TitleContainer>
              <TicketProductsTitle>{r.AddProductsToTicket}</TicketProductsTitle>
              <Tooltip text={r.ProductsNotReservedNotice} shiftOnMobile />
            </TitleContainer>

            <SubTitle>{r.TicketProductsTagline}</SubTitle>
          </div>
          <Carousel nested label={carouselLabel}>{cardElements}</Carousel>
        </TicketProductsSection>
      ) : (
        <Section id="products-section">
          <div>
            <TitleContainer>
              <Title>{r.Products}</Title>
              <Tooltip text={r.ProductsNotReservedNotice} />
            </TitleContainer>

            <SubTitle>{r.ProductsTagline}</SubTitle>
          </div>
          <Carousel label={carouselLabel}>{cardElements}</Carousel>
        </Section>
      )}

      <ProductDrawer
        open={isProductDrawerOpen}
        onClose={closeProductDrawer}
        product={drawerProduct}
        actions={
          drawerProduct &&
          // SwipeableDrawer keeps its content mounted after closing, so
          // isProductDrawerOpen is passed as `interactive`: otherwise the
          // hidden drawer would also render the popup and take popupRef from
          // the card, and the card's click-outside check would close it
          // before an option click lands.
          renderActions(drawerProduct, isProductDrawerOpen)
        }
      />

      <Overlay
        handleClose={handleAgeDeclined}
        open={isAgeVerificationOpen}
        size="sm"
        id={`age-verification${idSuffix}`}
        titleText={r.AgeVerification}
      >
        <AgeVerificationModal
          onConfirm={handleAgeConfirmed}
          onDecline={handleAgeDeclined}
        />
      </Overlay>

      <ProductDescriptionModal
        open={isDescriptionModalOpen}
        onClose={closeDescriptionModal}
        id={`product-description${idSuffix}`}
        product={descriptionModalProduct}
        actions={
          descriptionModalProduct &&
          renderActions(descriptionModalProduct, isDescriptionModalOpen)
        }
      />
    </>
  );
});
